import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { accountingAPI } from '../../services/api';

const AccountingDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [paymentBreakdown, setPaymentBreakdown] = useState([]);
  const [revenueByType, setRevenueByType] = useState([]);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    loadDashboardData();
  }, [startDate, endDate]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const params = { start_date: startDate, end_date: endDate };

      const [statsRes, trendRes, paymentRes, revenueRes] = await Promise.all([
        accountingAPI.getDashboardStats(params),
        accountingAPI.getSalesTrend({ days: 30 }),
        accountingAPI.getPaymentStatusBreakdown(params),
        accountingAPI.getRevenueByProductType(params),
      ]);

      setStats(statsRes.data.data);
      setSalesTrend(trendRes.data.data);
      setPaymentBreakdown(paymentRes.data.data);
      setRevenueByType(revenueRes.data.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">
            {t('accounting.dashboard.title')}
          </h2>
          <p className="text-gray-600 mt-1">{t('accounting.dashboard.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Link
            to="/accounting/sales-report"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('accounting.viewReports')}
          </Link>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="bg-white rounded-lg shadow p-4 flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.startDate')}
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('common.endDate')}
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Total Revenue */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">
                  {t('accounting.totalRevenue')}
                </p>
                <p className="text-3xl font-bold text-blue-900 mt-2">
                  {t('common.currency')} {stats.total_revenue?.toFixed(2)}
                </p>
              </div>
              <div className="bg-blue-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total VAT */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">
                  {t('accounting.totalVAT')}
                </p>
                <p className="text-3xl font-bold text-yellow-900 mt-2">
                  {t('common.currency')} {stats.total_vat?.toFixed(2)}
                </p>
              </div>
              <div className="bg-yellow-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Discounts */}
          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-red-600">
                  {t('accounting.totalDiscounts')}
                </p>
                <p className="text-3xl font-bold text-red-900 mt-2">
                  {t('common.currency')} {stats.total_discounts?.toFixed(2)}
                </p>
              </div>
              <div className="bg-red-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Total Fees */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">
                  {t('accounting.totalFees')}
                </p>
                <p className="text-3xl font-bold text-green-900 mt-2">
                  {t('common.currency')} {stats.total_fees?.toFixed(2)}
                </p>
              </div>
              <div className="bg-green-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Paid Amount */}
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">
                  {t('accounting.paidAmount')}
                </p>
                <p className="text-3xl font-bold text-emerald-900 mt-2">
                  {t('common.currency')} {stats.paid_amount?.toFixed(2)}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  {stats.paid_orders} {t('common.orders')}
                </p>
              </div>
              <div className="bg-emerald-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Outstanding Amount */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">
                  {t('accounting.outstanding')}
                </p>
                <p className="text-3xl font-bold text-orange-900 mt-2">
                  {t('common.currency')} {stats.outstanding_amount?.toFixed(2)}
                </p>
                <p className="text-xs text-orange-600 mt-1">
                  {stats.unpaid_orders + stats.partial_orders} {t('common.orders')}
                </p>
              </div>
              <div className="bg-orange-600 rounded-full p-3 text-white">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Trend Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('accounting.salesTrend')}
          </h3>
          <div className="space-y-2">
            {salesTrend.length > 0 ? (
              salesTrend.map((item, idx) => {
                const maxRevenue = Math.max(...salesTrend.map(x => x.revenue));
                const percentage = maxRevenue > 0 ? (item.revenue / maxRevenue) * 100 : 0;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-16 text-sm text-gray-600">{item.formatted_date}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <div className="w-20 text-right text-sm font-semibold text-gray-900">
                      {t('common.currency')} {item.revenue.toFixed(0)}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>
            )}
          </div>
        </div>

        {/* Payment Status Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('accounting.paymentStatusBreakdown')}
          </h3>
          <div className="space-y-3">
            {paymentBreakdown.length > 0 ? (
              paymentBreakdown.map((item, idx) => {
                const colors = {
                  'paid': 'bg-green-500',
                  'partial': 'bg-yellow-500',
                  'unpaid': 'bg-red-500',
                };
                const totalAmount = paymentBreakdown.reduce((sum, x) => sum + x.amount, 0);
                const percentage = totalAmount > 0 ? (item.amount / totalAmount) * 100 : 0;

                return (
                  <div key={idx}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 capitalize">
                        {item.status} ({item.count})
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {t('common.currency')} {item.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${colors[item.status] || 'bg-gray-400'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-8">{t('common.noData')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Revenue by Product Type */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('accounting.revenueByProductType')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {revenueByType.length > 0 ? (
            revenueByType.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-600 capitalize">{item.type}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">
                  {t('common.currency')} {item.amount.toFixed(2)}
                </p>
                <p className="text-xs text-gray-500 mt-1">{item.count} {t('common.orders')}</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-center py-8 col-span-4">{t('common.noData')}</p>
          )}
        </div>
      </div>

      {/* Quick Links */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('accounting.quickLinks')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/accounting/sales-report" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <h4 className="font-semibold text-gray-900">{t('accounting.salesReports')}</h4>
            <p className="text-sm text-gray-600 mt-1">{t('accounting.viewSalesDetails')}</p>
          </Link>
          <Link to="/accounting/vat-report" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <h4 className="font-semibold text-gray-900">{t('accounting.vatReports')}</h4>
            <p className="text-sm text-gray-600 mt-1">{t('accounting.viewVATDetails')}</p>
          </Link>
          <Link to="/accounting/payment-tracking" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <h4 className="font-semibold text-gray-900">{t('accounting.paymentTracking')}</h4>
            <p className="text-sm text-gray-600 mt-1">{t('accounting.trackPayments')}</p>
          </Link>
          <Link to="/accounting/financial-summary" className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <h4 className="font-semibold text-gray-900">{t('accounting.financialSummary')}</h4>
            <p className="text-sm text-gray-600 mt-1">{t('accounting.viewFinancials')}</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccountingDashboard;
